import { useId, type ButtonHTMLAttributes, type ReactNode, type Ref } from "react";
import { useCopy } from "../hooks/useCopy";
import { useI18n } from "../i18n";
import { chainInfo } from "../lib/chains";
import { localizeDecimal } from "../lib/money";
import type { ChainId, InvoiceStatus } from "../lib/types";
import { IconAlert, IconCheck, IconCopy, IconExternal, IconInfo } from "./icons";

export function Spinner({ size = 18, label }: { size?: number; label?: string }) {
  return (
    <span className="spinner" style={{ width: size, height: size }} role={label ? "status" : undefined} aria-label={label}>
      <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
        <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2.5" />
        <path d="M21.5 12a9.5 9.5 0 0 0-9.5-9.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </span>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  ref?: Ref<HTMLButtonElement>;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "md" | "lg";
  loading?: boolean;
  block?: boolean;
};

export function Button({ variant = "primary", size = "md", loading, block, className = "", children, disabled, ...rest }: ButtonProps) {
  return (
    <button
      className={`btn btn-${variant} btn-${size}${block ? " btn-block" : ""} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === "lg" ? 18 : 16} /> : null}
      <span className="btn-label">{children}</span>
    </button>
  );
}

export function LinkButton({
  href,
  variant = "secondary",
  size = "md",
  block,
  external,
  children,
}: {
  href: string;
  variant?: "primary" | "secondary" | "ghost";
  size?: "md" | "lg";
  block?: boolean;
  external?: boolean;
  children: ReactNode;
}) {
  return (
    <a
      className={`btn btn-${variant} btn-${size}${block ? " btn-block" : ""}`}
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      <span className="btn-label">{children}</span>
      {external ? <IconExternal size={16} /> : null}
    </a>
  );
}

export function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a className="link" href={href} target="_blank" rel="noopener noreferrer">
      {children}
      <IconExternal size={14} />
    </a>
  );
}

/** A figure with tabular numerals; the fraction can be de-emphasised. */
export function Amount({
  value,
  symbol,
  className = "",
  emphasiseFrom,
}: {
  /** Plain decimal string ("25.001234"). */
  value: string;
  symbol?: string;
  className?: string;
  /** Fraction digits from this index on are highlighted (the exchange-amount suffix). */
  emphasiseFrom?: number;
}) {
  const { locale } = useI18n();
  const { whole, fraction, decimal } = localizeDecimal(value, locale);
  const plain = fraction.slice(0, emphasiseFrom ?? fraction.length);
  const marked = emphasiseFrom !== undefined ? fraction.slice(emphasiseFrom) : "";
  return (
    <span className={`amount num ${className}`}>
      <span className="amount-whole">{whole}</span>
      {fraction ? (
        <span className="amount-fraction">
          {decimal}
          {plain}
          {marked ? <mark className="amount-suffix">{marked}</mark> : null}
        </span>
      ) : null}
      {symbol ? <span className="amount-symbol"> {symbol}</span> : null}
    </span>
  );
}

export function ChainBadge({ chain }: { chain: ChainId }) {
  const { t } = useI18n();
  const info = chainInfo(chain);
  return (
    <span className={`chain-badge chain-${info.family}`}>
      <span className="chain-dot" aria-hidden="true" />
      <span>{info.name}</span>
      {info.testnetLabel ? (
        <span className="chain-testnet" title={t("common.testnetNote")}>
          {info.testnetLabel}
        </span>
      ) : null}
    </span>
  );
}

const STATUS_TONE: Record<InvoiceStatus, "neutral" | "info" | "success" | "warning" | "danger"> = {
  pending: "neutral",
  confirming: "info",
  paid: "success",
  overpaid: "success",
  partially_paid: "warning",
  underpaid: "warning",
  expired: "neutral",
  flagged: "warning",
};

export type Tone = "neutral" | "info" | "success" | "warning" | "danger";

/** A status pill. Keyed on its label so a state change re-mounts it with a short entrance. */
export function Pill({ tone, label, size = "md", live }: { tone: Tone; label: string; size?: "md" | "lg"; live?: boolean }) {
  return (
    <span key={label} className={`pill pill-${tone} pill-${size}${live ? " pill-live" : ""}`}>
      <span className="pill-dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export function StatusPill({ status, size = "md" }: { status: InvoiceStatus; size?: "md" | "lg" }) {
  const { t } = useI18n();
  return <Pill tone={STATUS_TONE[status]} label={t(`status.${status}`)} size={size} live={status === "confirming"} />;
}

/** Labelled form field with hint and error wired to the control through aria attributes. */
export function Field({
  label,
  hint,
  error,
  optional,
  children,
  id,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  id: string;
  children: (a11y: { id: string; "aria-invalid": boolean | undefined; "aria-describedby": string | undefined }) => ReactNode;
}) {
  const { t } = useI18n();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`field${error ? " has-error" : ""}`}>
      <label className="field-top" htmlFor={id}>
        <span className="field-name">{label}</span>
        {optional ? <span className="field-optional">{t("common.optional")}</span> : null}
      </label>
      {children({ id, "aria-invalid": error ? true : undefined, "aria-describedby": describedBy })}
      {error ? (
        <p className="field-error" id={errId}>
          <IconAlert size={14} />
          <span>{error}</span>
        </p>
      ) : null}
      {hint ? (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function Notice({
  tone = "info",
  title,
  children,
  icon,
}: {
  tone?: "info" | "warning" | "danger" | "success";
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={`notice notice-${tone}`} role={tone === "danger" ? "alert" : undefined}>
      <span className="notice-icon">{icon ?? (tone === "info" ? <IconInfo /> : tone === "success" ? <IconCheck /> : <IconAlert />)}</span>
      <div className="notice-body">
        {title ? <div className="notice-title">{title}</div> : null}
        {children ? <div className="notice-text">{children}</div> : null}
      </div>
    </div>
  );
}

export function CopyButton({ value, label, compact }: { value: string; label: string; compact?: boolean }) {
  const { t } = useI18n();
  const { copied, copy } = useCopy();
  return (
    <>
      <button
        type="button"
        className={`copy-btn${copied ? " is-copied" : ""}${compact ? " copy-btn-compact" : ""}`}
        onClick={() => void copy(value)}
        aria-label={`${t("common.copy")}: ${label}`}
      >
        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
        {compact ? null : <span>{copied ? t("common.copied") : t("common.copy")}</span>}
      </button>
      <span className="sr-only" aria-live="polite">
        {copied ? t("common.copiedAria", { label }) : ""}
      </span>
    </>
  );
}

/** A labelled value the customer needs to copy exactly (amount, address). */
export function CopyField({
  label,
  value,
  display,
  mono,
  hint,
}: {
  label: string;
  value: string;
  display?: ReactNode;
  mono?: boolean;
  hint?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="copy-field">
      <div className="field-label" id={id}>
        {label}
      </div>
      <div className="copy-field-row" aria-labelledby={id}>
        <div className={`copy-field-value${mono ? " mono" : ""}`}>{display ?? value}</div>
        <CopyButton value={value} label={label} />
      </div>
      {hint ? <div className="field-hint">{hint}</div> : null}
    </div>
  );
}

export function KeyValue({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="kv">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function Skeleton({ width = "100%", height = 14, radius = 6 }: { width?: number | string; height?: number; radius?: number }) {
  return <span className="skeleton" style={{ width, height, borderRadius: radius }} aria-hidden="true" />;
}
